// Activity Tracker Hook
// Tracks user activity (mouse, keyboard, scroll, touch) with throttling

import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;
const THROTTLE_MS = 30_000; // Update at most once per 30 seconds

export interface ActivityTracker {
  isIdle: (idleMinutes: number) => boolean;
  getLastActivity: () => number;
  resetActivity: () => void;
}

export function useActivityTracker(): ActivityTracker {
  const lastActivityRef = useRef<number>(Date.now());
  const lastUpdateRef = useRef<number>(0);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    // Throttle: only update if enough time has passed since last update
    if (now - lastUpdateRef.current >= THROTTLE_MS) {
      lastActivityRef.current = now;
      lastUpdateRef.current = now;
    }
  }, []);

  useEffect(() => {
    // Set initial activity timestamp
    lastActivityRef.current = Date.now();
    lastUpdateRef.current = Date.now();

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
    };
  }, [handleActivity]);

  const isIdle = useCallback((idleMinutes: number): boolean => {
    const idleMs = idleMinutes * 60 * 1000;
    return Date.now() - lastActivityRef.current >= idleMs;
  }, []);

  const getLastActivity = useCallback((): number => {
    return lastActivityRef.current;
  }, []);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastUpdateRef.current = Date.now();
  }, []);

  return { isIdle, getLastActivity, resetActivity };
}
